import { Group, Icon, Paragraph, createStyles } from '@ingeniorforeningen/figurine-core';
import {
  getFormattedDateFromString,
  showPhoneNumberForCaseWorker,
} from '../../helpers/casesHelpers';
import { AppLanguage, CaseDetails, DetailsTexts } from '../../types';
import { stripText } from '../../helpers/textsHelpers';
import './PrintStyling.css';

const style = createStyles((theme) => ({
  smallParagraph: {
    fontSize: '0.75em',
    lineHeight: '1em',
    width: '100%',
  },
}));

type CaseInformationProps = {
  caseDetails: CaseDetails;
  texts: DetailsTexts;
  language: AppLanguage;
};

type TextRowProps = {
  title: string;
  text: string;
};

type CaseWorkerRowProps = {
  title: string;
  name: string;
  caseWorkerTitle?: string;
  phone?: string;
  department: string;
};

const CaseInformation = ({ caseDetails, texts, language }: CaseInformationProps) => {
  const { classes } = style();

  const DetailsTextRow = ({ title, text }: TextRowProps) => {
    return (
      <Group spacing={0}>
        <Paragraph className={classes.smallParagraph} text={title} align={'left'} />
        <Paragraph text={text} align={'left'} />
      </Group>
    );
  };

  const CaseWorkerRow = ({
    title,
    name,
    caseWorkerTitle,
    phone,
    department,
  }: CaseWorkerRowProps) => {
    return (
      <>
        <Group spacing={0}>
          <Paragraph className={classes.smallParagraph} text={title} align={'left'} />
          <Paragraph text={name} align={'left'} />
          {caseWorkerTitle && (
            <Paragraph
              cypress="caseWorkerTitle"
              style={{ width: '100%', fontStyle: 'italic' }}
              text={caseWorkerTitle}
              align={'left'}
            />
          )}
        </Group>
        {showPhoneNumberForCaseWorker(department, phone) && (
          <Group noWrap cypress="phoneNumberRow">
            <Icon name="Phone" />
            <Paragraph text={phone} />
          </Group>
        )}
      </>
    );
  };

  return (
    <Group p="xl" direction="column" spacing="lg" id="case-information">
      {caseDetails.categoryName && (
        <DetailsTextRow title={stripText(texts.category)} text={caseDetails.categoryName} />
      )}
      {caseDetails.subCategoryName && (
        <DetailsTextRow title={stripText(texts.subCategory)} text={caseDetails.subCategoryName} />
      )}
      <DetailsTextRow title={stripText(texts.caseNumber)} text={caseDetails.caseNumber} />
      <DetailsTextRow
        title={stripText(texts.creationDate)}
        text={getFormattedDateFromString(caseDetails.creationDate, language)}
      />
      {caseDetails.employerName && (
        <DetailsTextRow title={stripText(texts.employerName)} text={caseDetails.employerName} />
      )}
      {caseDetails.employmentType && (
        <DetailsTextRow
          title={stripText(texts.employmentType)}
          text={caseDetails.employmentTypeName}
        />
      )}
      {caseDetails.caseOwner && (
        <CaseWorkerRow
          title={stripText(texts.caseWorker)}
          name={caseDetails.caseOwner.name}
          caseWorkerTitle={caseDetails.caseOwner.title}
          phone={caseDetails.caseOwner.phone}
          department={caseDetails.department}
        />
      )}
    </Group>
  );
};

export default CaseInformation;
